import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

/**
 * Generic dropdown shell. Owns the trigger → panel chrome (positioning,
 * open/close, outside-click, escape, animation). Content-agnostic — trigger
 * and panel body come from content projection via:
 *   <button gs-dd-trigger>…</button>
 *   <div   gs-dd-panel>…</div>
 *
 * The panel's visual styling (background, border, shadow, radius) lives in
 * this component's SCSS. Item-level styles (.gs-dd-*) live in the shared
 * `src/styles/dropdown.scss` partial so any consumer can reuse them.
 */
@Component({
  selector: 'gs-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent {
  @Input() align: 'left' | 'right' = 'right';
  @Input() width = '268px';
  @Input() offsetTop = '12px';

  @Output() openChange = new EventEmitter<boolean>();

  open = false;

  constructor(private host: ElementRef<HTMLElement>) {}

  toggle(_e: MouseEvent) {
    // Do NOT stopPropagation — the click must still reach `document` so any
    // other open <gs-dropdown> sees the outside click and closes itself.
    // Our own `onDocClick` guards against self-closing via host.contains().
    this.setOpen(!this.open);
  }

  close() {
    this.setOpen(false);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (!this.open) return;
    if (!this.host.nativeElement.contains(e.target as Node)) this.setOpen(false);
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.open) this.setOpen(false);
  }

  private setOpen(v: boolean) {
    if (this.open === v) return;
    this.open = v;
    this.openChange.emit(v);
  }
}
